import {Button} from '@primer/react'
import {PinIcon} from '@primer/octicons-react'
import {useEffect} from 'react'
import {announce} from '@github-ui/aria-live'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import type {usePinnedSidebarTasks} from '../../hooks/use-task-pins'
import {generateTaskUrlForRepo, shouldUseGlobalTaskUrls} from '../../utils/generate-task-path'
import {SessionsNavItem} from './SessionsByRepoNav/SessionsNavItem'
import stateStyles from './SidebarState.module.css'
import styles from './AgentsSidebar.module.css'

export type PinnedSessionsState = ReturnType<typeof usePinnedSidebarTasks>

function CollapsedPinIcon(props: {size?: number; className?: string}) {
  const {isExpanded} = useCollapsibleSidebarContext()
  return isExpanded ? null : <PinIcon {...props} />
}

export function PinnedSessions({
  pins,
  activeTaskId,
  onNavigate,
}: {
  pins: PinnedSessionsState
  activeTaskId?: string
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const globalUrls = shouldUseGlobalTaskUrls({
    taskUrlScope: 'global',
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
    repositoryTaskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_repository_task_detail'),
  })
  const taskSearchParams = new URLSearchParams(searchParams)
  taskSearchParams.delete('session_id')
  const errorMessage = pins.isError
    ? 'Couldn’t refresh pinned sessions.'
    : pins.hasHydrationError
      ? 'Couldn’t load some pinned session details.'
      : undefined
  useEffect(() => {
    if (errorMessage) announce(errorMessage)
  }, [errorMessage])
  if (pins.isLoading || (pins.tasks.length === 0 && !errorMessage)) return null

  return (
    <CollapsibleNavList.Group
      title="Pinned"
      data-task-pins-list
      icon={CollapsedPinIcon}
      expandedHeading
      headingLevel="h2"
      showDivider
      popoverWidth="medium"
      className={styles.sessionsListGroup}
    >
      {errorMessage && (
        <li className={stateStyles.state}>
          <p className={stateStyles.stateDescription}>{errorMessage}</p>
          <Button size="small" variant="invisible" data-task-pins-retry onClick={() => void pins.refetch()}>
            Retry pinned sessions
          </Button>
        </li>
      )}
      {pins.tasks.map(task => {
        const href = generateTaskUrlForRepo({
          taskId: task.id,
          repositoryNwo: globalUrls ? undefined : task.nwo,
          sessionId: task.latest_session_id,
          searchParams: taskSearchParams,
        })
        if (!href) return null
        return (
          <SessionsNavItem
            key={task.id}
            task={task}
            href={href}
            enablePinning
            isSelected={task.id === activeTaskId}
            onClick={event => {
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link behavior.
              const hasModifierKey = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
              if (event.defaultPrevented || event.button !== 0 || hasModifierKey) return
              event.preventDefault()
              navigate(href)
              onNavigate?.()
            }}
          />
        )
      })}
    </CollapsibleNavList.Group>
  )
}
